/**
 * Recolour an element's border without fighting React over the same property.
 *
 * WHY THIS EXISTS. Hover and focus states across the dashboard were written as
 * `e.currentTarget.style.borderColor = '…'` on elements whose React style sets the
 * `border` SHORTHAND. React warns about exactly that pairing:
 *
 *   Warning: Updating a style property during rerender (border) when a conflicting
 *   property is set (borderColor) can lead to styling bugs.
 *
 * The bug it warns about is real and lands where it is least welcome. Most of these
 * style objects are built inside the component, so they are a new object on every
 * render and React re-applies `border` on every render. Type into a form that
 * rerenders per keystroke and the focus ring you are looking at gets reset out from
 * under you — on the input you are currently using.
 *
 * The fix is to write the property React owns. Callers that KNOW their element's
 * border can just write it inline. This is for the shared hover/focus helpers, which
 * are attached to elements with different borders and cannot know.
 *
 * It never invents a width or a style: both are read back from the element, so the
 * only thing that changes is the colour. A border that was 1.5px dashed stays 1.5px
 * dashed.
 */
export function setBorderColor(el: HTMLElement, color: string): void {
  // jsdom and older engines can return an empty computed style; writing
  // `border: " solid red"` from that would erase the border entirely.
  const cs = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null;
  const w = cs?.borderTopWidth;
  const s = cs?.borderTopStyle;
  if (!w || !s || s === 'none') {
    // Nothing readable to preserve. Setting the longhand here is the lesser evil: it
    // is what the caller asked for, and an element with no border to preserve is not
    // the one React is fighting over.
    el.style.borderColor = color;
    return;
  }

  // A per-side border must not be flattened into the four-sided shorthand — that
  // would give an element with only a bottom rule a full box.
  const uniform =
    cs.borderRightWidth === w &&
    cs.borderBottomWidth === w &&
    cs.borderLeftWidth === w &&
    cs.borderRightStyle === s &&
    cs.borderBottomStyle === s &&
    cs.borderLeftStyle === s;

  if (uniform) {
    el.style.border = `${w} ${s} ${color}`;
    return;
  }

  // Sides differ. Write each side's own shorthand, preserving that side's width and
  // style — still a shorthand per side, so still not the longhand React objects to.
  el.style.borderTop = `${w} ${s} ${color}`;
  el.style.borderRight = `${cs.borderRightWidth} ${cs.borderRightStyle} ${color}`;
  el.style.borderBottom = `${cs.borderBottomWidth} ${cs.borderBottomStyle} ${color}`;
  el.style.borderLeft = `${cs.borderLeftWidth} ${cs.borderLeftStyle} ${color}`;
}
